import { Button, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import clsx from "clsx";
import { ReactNode, useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { HowItWorksButton } from "@/components/tutorials/how-it-works-button";
import {
  PageHeaderActionsContext,
  PageTitleContext,
} from "@/contexts/page-layout";
import { useSidebarLayoutStore } from "@/stores/sidebar-layout";

interface PageLayoutProps {
  // Optional: tab-level pages (e.g. channel manage) omit it because the tab
  // already names the section - they show only a description + CTA.
  title?: ReactNode;
  // Ids for the How it works button. Pages list their own; nothing is inferred from the route.
  tutorials?: readonly number[];
  children: ReactNode;
  buttons?: Array<ReactNode>;
  // A full-width row under the title, inside the header - a report's dates and filters.
  toolbar?: ReactNode;
  // Pins the header and this footer; only the content between them scrolls.
  footer?: ReactNode;
  // The page does not scroll; its content fills the space under the header and scrolls inside itself.
  isFillHeight?: boolean;
  description?: ReactNode;
  headerLeading?: ReactNode;
  showBack?: boolean;
  onBackUrl?: string;
  onBackClick?: () => void;
  className?: string;
  containerClassName?: string;
  // A max width (`max-w-4xl`) for a page whose body is a narrow centered
  // column: the header joins that column instead of spanning the page, and the
  // description wraps because one line no longer fits.
  columnClassName?: string;
  headerClassName?: string;
  actionsClassName?: string;
  // "compact" renders a smaller, tightly-spaced header - used where the page
  // already sits under section chrome (e.g. the channel manage tab strip).
  variant?: "default" | "compact";
}

export const PageLayout = ({
  children,
  title,
  tutorials,
  buttons,
  toolbar,
  footer,
  isFillHeight,
  description,
  headerLeading,
  showBack,
  onBackUrl,
  onBackClick,
  className,
  containerClassName,
  columnClassName,
  headerClassName,
  actionsClassName,
  variant = "default",
}: PageLayoutProps) => {
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar, setHasPinnedFooter } =
    useSidebarLayoutStore();

  const sectionActions = useContext(PageHeaderActionsContext);

  const isCompact = variant === "compact";
  const isPinned = Boolean(footer);
  const titleText = typeof title === "string" ? title : undefined;
  const columnClasses =
    columnClassName && clsx("mx-auto w-full", columnClassName);

  const handleBack = useCallback(() => {
    if (onBackClick) {
      onBackClick();
    } else if (onBackUrl) {
      navigate(onBackUrl);
    } else {
      navigate(-1);
    }
  }, [navigate, onBackUrl, onBackClick]);

  useEffect(() => {
    if (!isPinned) return;
    setHasPinnedFooter(true);

    return () => setHasPinnedFooter(false);
  }, [isPinned, setHasPinnedFooter]);

  return (
    <div
      className={clsx(
        "flex flex-col w-full h-full relative bg-background",
        isPinned || isFillHeight ? "overflow-hidden" : "overflow-auto",
        className,
      )}
    >
      <header
        className={clsx(
          "flex shrink-0 items-center gap-3 justify-between bg-background",
          toolbar && "flex-wrap",
          isCompact ? "px-4 py-2" : "pt-4 py-2 px-4",
          columnClasses,
          headerClassName,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {!isSidebarOpen && (
            <Button
              isIconOnly
              size="sm"
              variant="tertiary"
              onPress={toggleSidebar}
            >
              <Icon fontSize={20} icon="solar:sidebar-minimalistic-linear" />
            </Button>
          )}
          {showBack && (
            <Icon
              className="cursor-pointer hover:text-muted p-1"
              fontSize={isCompact ? 26 : 34}
              icon={"solar:alt-arrow-left-linear"}
              onClick={handleBack}
            />
          )}
          {(headerLeading || title || description) && (
            <div className="flex min-w-0 items-center gap-2">
              {headerLeading && <div className="shrink-0">{headerLeading}</div>}
              {(title || description) && (
                <div className={clsx("min-w-0", isCompact && "leading-tight")}>
                  {title && (
                    <Typography
                      className="truncate"
                      type={isCompact ? "h6" : "h4"}
                    >
                      {title}
                    </Typography>
                  )}
                  {description &&
                    (typeof description === "string" ? (
                      <Typography
                        className={clsx(
                          isCompact && "leading-tight",
                          // Title-less compact header: the description is the primary
                          // line, so nudge it up a touch from body-sm.
                          isCompact && !title && "text-[15px]",
                          !columnClassName && "truncate",
                        )}
                        color="muted"
                        type={isCompact && title ? "body-xs" : "body-sm"}
                      >
                        {description}
                      </Typography>
                    ) : (
                      // Rich descriptions (e.g. a metadata row with chips) render
                      // as-is so they aren't forced into a single muted paragraph.
                      <div className="mt-0.5">{description}</div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          className={clsx(
            "ml-auto flex w-fit shrink-0 items-center gap-2",
            actionsClassName,
          )}
          id="page-header-actions"
        >
          {tutorials && <HowItWorksButton tutorialIds={tutorials} />}
          {buttons}
          <PageTitleContext.Provider value={titleText}>
            {sectionActions}
          </PageTitleContext.Provider>
        </div>
        {toolbar && <div className="min-w-0 basis-full">{toolbar}</div>}
      </header>

      <main
        className={clsx(
          "w-full flex-1",
          isPinned && "min-h-0 overflow-auto",
          isFillHeight && !isPinned && "min-h-0 overflow-hidden",
          isCompact ? "px-4 pb-4 pt-1" : "p-4",
          columnClasses,
          containerClassName,
        )}
      >
        {children}
      </main>
      {footer && (
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3">
          {footer}
        </footer>
      )}
    </div>
  );
};
